import { getElementBottom, GridContainer, StickyBar } from '@bob/library'
import { AppProviderContext } from '@websrc/App/AppContext'
import { Asset } from '@websrc/App/Components/Asset/Asset'
import { LessonInfoText } from '@websrc/App/Components/Lesson/Info/LessonInfoText'
import { LessonMainNavigation } from '@websrc/App/Components/Lesson/Navigation/LessonMainNavigation'
import { Steps } from '@websrc/App/Components/Lesson/Steps/Steps'
import { LessonStudentMaterial } from '@websrc/App/Components/Lesson/StudentMaterial/LessonStudentMaterial'
import { LessonTeacherSupport } from '@websrc/App/Components/Lesson/TeacherSupport/LessonTeacherSupport'
import { PrintAndShare } from '@websrc/App/Components/PrintAndShare/PrintAndShare'
import { useStaticText } from '@websrc/App/Hooks/useStaticText'
import { matomo } from '@websrc/App/Tracking/Matomo'
import { mapLesson } from '@websrc/DataMappers/mapLesson'
import React, { useContext, useEffect, useRef, useState } from 'react'

import * as SC from './Lesson.style'

export type LessonProps = ReturnType<typeof mapLesson>

export function Lesson({
  mainNavigation,
  productNames,
  lessonInfo,
  lessonSteps,
  asset,
  teacherSupport,
  studentMaterialUrl,
  studentMaterialPdfLink,
}: LessonProps) {
  const { matomoPageTemplate } = useContext(AppProviderContext)
  const t = useStaticText()
  const mainNavRef = useRef<HTMLDivElement>(null)
  const [studentMaterialFixedScrollTopOffset, setStudentMaterialFixedScrollTopOffset] = useState(0)

  useEffect(() => {
    if (mainNavRef.current) {
      setStudentMaterialFixedScrollTopOffset(getElementBottom(mainNavRef.current))
    }
  }, [])

  return (
    <>
      <StickyBar bgColor={'corporateBlue'}>
        <LessonMainNavigation {...mainNavigation} wrapperRef={mainNavRef} />
      </StickyBar>
      <GridContainer>
        <SC.Wrapper noGutter={true}>
          <SC.HeaderContent desktop={7}>
            <LessonInfoText
              belowTitle={
                <PrintAndShare
                  onPrint={() =>
                    matomo.trackEvent(
                      'engagement',
                      matomoPageTemplate,
                      [lessonInfo.title],
                      [t.print_and_share.print_button],
                      'button'
                    )
                  }
                  onShare={() =>
                    matomo.trackEvent(
                      'engagement',
                      matomoPageTemplate,
                      [lessonInfo.title],
                      [t.print_and_share.share_button],
                      'button'
                    )
                  }
                />
              }
              lesson={lessonInfo}
              productNames={productNames}
            />
          </SC.HeaderContent>
          <SC.HeaderSide desktop={4} desktopOffset={1}>
            <Asset
              sizes={'(min-width: 1024px) 350px'}
              srcSetWidths={[320, 400, 600, 800, 1200]}
              {...asset}
            />
          </SC.HeaderSide>
        </SC.Wrapper>
        <SC.ContentWrapper noGutter={true}>
          <SC.Content desktop={7}>
            <Steps steps={lessonSteps} />
          </SC.Content>
          <SC.SideBar desktop={4} desktopOffset={1}>
            <LessonTeacherSupport {...teacherSupport} />
            {(studentMaterialUrl || studentMaterialPdfLink) && (
              <LessonStudentMaterial
                fixedScrollTopOffset={studentMaterialFixedScrollTopOffset}
                pdfLink={studentMaterialPdfLink}
                url={studentMaterialUrl}
              />
            )}
          </SC.SideBar>
        </SC.ContentWrapper>
      </GridContainer>
    </>
  )
}
